import { ArticleEditorPage } from "@brandex/dashboard";
import { formatRelative } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Select, Spinner } from "@brandex/ui";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useAdminArticles, useWebsites, type AdminArticle, type ArticleStatus } from "../data";
import { QueryError } from "../views";

const STATUS: Record<ArticleStatus, { label: string; tone: "neutral" | "accent" | "success" | "warning" }> = {
  draft: { label: "Draft", tone: "neutral" },
  in_review: { label: "Waiting for the doctor", tone: "accent" },
  published: { label: "Published", tone: "success" },
  unpublished: { label: "Unpublished", tone: "warning" },
};

/** An organization id is 24 hex characters. */
export const ID = /^[0-9a-f]{24}$/;

export interface ArticlesPageProps {
  /** The practice whose article is open. */
  org?: string | undefined;
  /** `new` or an article id. */
  edit?: string | undefined;
  /** Moves between the queue and the editor (the route keeps them in the address). */
  go: (to: { org?: string; edit?: string }, replace?: boolean) => void;
}

/**
 * Articles (F-32, ADR 0019): every doctor's articles in one place. BrandEx
 * editors write and edit for any doctor and send the article for review; only
 * the doctor publishes. Needs `platform.articles.manage`.
 */
export function ArticlesPage({ org, edit, go }: ArticlesPageProps) {
  if (org !== undefined && edit !== undefined && ID.test(org) && (edit === "new" || ID.test(edit))) {
    return <StaffEditor org={org} edit={edit} go={go} />;
  }
  return <Queue go={go} />;
}

function StaffEditor({ org, edit, go }: Required<Pick<ArticlesPageProps, "org" | "edit">> & Pick<ArticlesPageProps, "go">) {
  const qc = useQueryClient();
  const sites = useWebsites();
  const doctor = sites.data?.find((s) => s.organization_id === org)?.doctor_name;
  const leave = () => {
    // The queue must show what was just written.
    void qc.invalidateQueries({ queryKey: ["admin", "articles"] });
    go({});
  };
  return (
    <div className="grid gap-4">
      <p className="text-sm text-muted-foreground">
        Writing for <strong className="text-foreground">{doctor ?? "this doctor"}</strong>. They see it in their own Articles page and decide whether to publish.
      </p>
      <ArticleEditorPage
        // One editor for the whole session: a new article gets its id without remounting.
        key={org}
        orgId={org}
        id={edit === "new" ? null : edit}
        onClose={leave}
        onCreated={(id) => go({ org, edit: id }, true)}
      />
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Queue({ go }: Pick<ArticlesPageProps, "go">) {
  const [status, setStatus] = useState<"" | ArticleStatus>("");
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 300);
  const articles = useAdminArticles(status, q);
  const rows = articles.data ?? [];
  const waiting = status === "" && q === "" ? rows.filter((a) => a.status === "in_review").length : 0;

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Articles</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Articles on doctors' websites. Write or edit for a doctor and send it for review: only the doctor publishes, so nothing medical goes out in their name without their approval.
          Editing a live article takes it back to a draft until the doctor publishes it again.
        </p>
      </header>

      <NewForDoctor go={go} />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle>All articles</CardTitle>
            <CardDescription>Newest activity first.</CardDescription>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Search">
              <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Title, summary or tag" className="min-w-56" />
            </Field>
            <Field label="Status">
              <Select className="min-w-48" value={status} onChange={(e) => setStatus(e.target.value as "" | ArticleStatus)}>
                <option value="">Any status</option>
                {(Object.keys(STATUS) as ArticleStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS[s].label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </CardHeader>
        <CardContent>
          {articles.isPending ? <Spinner label="Loading…" /> : null}
          {articles.isError ? <QueryError error={articles.error} /> : null}
          {waiting > 0 ? <Alert className="mb-4">{waiting} article{waiting === 1 ? " is" : "s are"} waiting for the doctor to review.</Alert> : null}
          {articles.isSuccess && rows.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" aria-hidden /> No articles{status !== "" || q !== "" ? " match" : " yet"}.
            </p>
          ) : null}
          {rows.length > 0 ? <ArticleTable rows={rows} onOpen={(a) => go({ org: a.organization_id, edit: a.id })} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}

function ArticleTable({ rows, onOpen }: { rows: AdminArticle[]; onOpen: (a: AdminArticle) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] text-sm">
        <thead>
          <tr className="border-b border-border text-start text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="py-2 pe-3 text-start font-medium">Article</th>
            <th scope="col" className="px-3 py-2 text-start font-medium">Doctor</th>
            <th scope="col" className="px-3 py-2 text-start font-medium">Status</th>
            <th scope="col" className="px-3 py-2 text-start font-medium">Language</th>
            <th scope="col" className="px-3 py-2 text-start font-medium">Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-b border-border/60 align-top">
              <td className="py-3 pe-3">
                <button type="button" onClick={() => onOpen(a)} className="text-start font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring" lang={a.locale}>
                  {a.title}
                </button>
                <p className="text-xs text-muted-foreground">/blog/{a.slug} · {a.reading_minutes} min read</p>
              </td>
              <td className="px-3 py-3">{a.doctor_name ?? "—"}</td>
              <td className="px-3 py-3">
                <Badge tone={STATUS[a.status].tone}>{STATUS[a.status].label}</Badge>
              </td>
              <td className="px-3 py-3">{a.locale === "bn" ? "বাংলা" : "English"}</td>
              <td className="px-3 py-3 text-muted-foreground">{formatRelative("en", a.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewForDoctor({ go }: Pick<ArticlesPageProps, "go">) {
  const sites = useWebsites();
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = sites;
  // The picker needs every doctor, so keep loading pages.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const doctors = useMemo(
    () => (sites.data ?? []).filter((s) => s.doctor_name).sort((a, b) => (a.doctor_name ?? "").localeCompare(b.doctor_name ?? "")),
    [sites.data],
  );
  const [org, setOrg] = useState("");
  return (
    <Card>
      <CardHeader>
        <CardTitle>Write for a doctor</CardTitle>
        <CardDescription>Starts a draft on the doctor's behalf. They review it before it goes live.</CardDescription>
      </CardHeader>
      <CardContent>
        {sites.isError ? <QueryError error={sites.error} /> : null}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (org !== "") go({ org, edit: "new" });
          }}
        >
          <Field label="Doctor">
            <Select className="min-w-64" value={org} onChange={(e) => setOrg(e.target.value)} disabled={sites.isPending}>
              <option value="">Choose a doctor…</option>
              {doctors.map((d) => (
                <option key={d.organization_id} value={d.organization_id}>
                  {d.doctor_name}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" disabled={org === ""}>
            <Plus className="h-4 w-4" aria-hidden /> New article
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
